import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, Clock, Lock, Phone, LifeBuoy, Droplet, Thermometer,
  Zap, Waves, AlertTriangle, Gauge, Check, ArrowRight, ArrowLeft,
} from "lucide-react";
import { Navbar } from "@/components/site/Navbar";

const TRANSITION_DURATION_MS = 1500;

const INFO_CARDS = [
  {
    icon: Clock,
    tag: "Always Available",
    title: "24/7 Emergency Response",
    body: "Unexpected equipment failure, leaks, livestock concerns, or urgent system issues can happen at any time. Our team is available whenever you need us.",
  },
  {
    icon: Lock,
    tag: "Exclusive Access",
    title: "Maintenance Clients Only",
    body: "Emergency support is reserved exclusively for customers currently enrolled in one of our Aquarium Maintenance Programs.",
  },
  {
    icon: ShieldCheck,
    tag: "Peace of Mind",
    title: "Your Aquarium Is Protected",
    body: "Knowing experienced professionals are only a phone call away means you can enjoy your aquarium without worrying about unexpected emergencies.",
  },
];

const EMERGENCIES = [
  { icon: Droplet, label: "Major leak" },
  { icon: Gauge, label: "Pump failure" },
  { icon: Waves, label: "Overflow malfunction" },
  { icon: Zap, label: "Power outage affecting filtration" },
  { icon: AlertTriangle, label: "Rapid livestock distress" },
  { icon: Thermometer, label: "Temperature emergency" },
  { icon: LifeBuoy, label: "Life support equipment failure" },
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function EmergencySupport() {
  const [phase, setPhase] = useState("intro"); // "intro" | "page"
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
  }, []);

  useEffect(() => {
    if (phase !== "intro") return;
    const t = setTimeout(() => setPhase("page"), TRANSITION_DURATION_MS);
    return () => clearTimeout(t);
  }, [phase]);

  const goHome = (target) => {
    navigate("/");
    setTimeout(() => {
      const el = document.querySelector(target);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { duration: 1.4 });
      else el.scrollIntoView({ behavior: "smooth" });
    }, 250);
  };

  return (
    <main data-testid="emergency-page" className="relative min-h-screen bg-[var(--ink)] text-[var(--foam)]">
      {/* Cinematic intro transition (plays once) */}
      {phase === "intro" && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-[var(--ink)] cursor-pointer"
          data-testid="emergency-intro"
          onClick={() => setPhase("page")}
        >
          <video
            className="absolute inset-0 h-full w-full object-cover"
            src="/media/emergency-transition.mp4"
            autoPlay
            muted
            playsInline
            onEnded={() => setPhase("page")}
            data-testid="emergency-transition-video"
          />
        </motion.div>
      )}

      {/* Looping background video + dark overlay */}
      <div className="fixed inset-0 z-0">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          src="/media/emergency-loop.mp4"
          autoPlay
          loop
          muted
          playsInline
          data-testid="emergency-loop-video"
        />
        <div className="absolute inset-0 bg-[#0a0d20]/82" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#090b1c]/90 via-[#0d1026]/70 to-[#090b1c]/95" />
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: phase === "page" ? 1 : 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10"
      >
        <Navbar />

        {/* HERO */}
        <section className="relative flex min-h-[58vh] items-center overflow-hidden px-6 lg:px-16 pt-52 pb-10">
          <div className="relative mx-auto flex w-full max-w-4xl flex-col items-center text-center">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-[var(--aqua)]/40 bg-[var(--aqua)]/10 px-4 py-2 font-body text-xs font-medium uppercase tracking-[0.28em] text-[var(--aqua)]" data-testid="emergency-kicker">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--aqua)]" />
                Premium Concierge Service
              </span>
            </Reveal>

            <Reveal delay={0.1}>
              <h1 className="mt-6 font-title text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl" data-testid="emergency-headline">
                24/7 Emergency<br />Aquarium Support
              </h1>
            </Reveal>

            <Reveal delay={0.2}>
              <p className="mt-6 max-w-2xl font-body text-base font-light leading-relaxed text-[var(--foam-dim)] lg:text-lg">
                Seatech Aquariums provides 24/7 emergency support for active maintenance
                clients, giving you peace of mind whenever unexpected issues arise.
              </p>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => goHome("#contact")}
                  data-testid="emergency-hero-cta"
                  className="group inline-flex items-center gap-3 rounded-full bg-[var(--aqua)] px-8 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[var(--ink-900)] transition-all duration-300 hover:shadow-[0_0_40px_-8px_var(--aqua)]"
                >
                  <Phone className="h-4 w-4" />
                  Call Emergency Support
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        {/* INFO CARDS */}
        <section className="px-6 lg:px-16 pb-24">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-6 md:grid-cols-3">
              {INFO_CARDS.map((card, i) => (
                <Reveal key={card.title} delay={i * 0.12}>
                  <div
                    className="group h-full rounded-3xl border border-[var(--foam)]/12 bg-[#0b1024]/45 p-8 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[var(--aqua)]/40"
                    data-testid={`emergency-info-card-${i}`}
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[var(--aqua)]/25 bg-[var(--aqua)]/10 text-[var(--aqua)] transition-all duration-500 group-hover:shadow-[0_0_30px_-6px_var(--aqua)]">
                      <card.icon className="h-7 w-7" strokeWidth={1.5} />
                    </div>
                    <span className="mt-6 block font-body text-xs font-medium uppercase tracking-[0.24em] text-[var(--aqua)]">
                      {card.tag}
                    </span>
                    <h3 className="mt-2 font-title text-xl font-bold tracking-tight">{card.title}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)]">
                      {card.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* WHAT COUNTS AS EMERGENCY */}
        <section className="px-6 lg:px-16 pb-24">
          <div className="mx-auto max-w-5xl">
            <Reveal>
              <h2 className="text-center font-title text-3xl font-bold tracking-tight lg:text-4xl" data-testid="emergency-list-heading">
                What Counts as an Emergency?
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-center font-body text-sm font-light leading-relaxed text-[var(--foam-dim)] lg:text-base">
                If any of the following happen, don't wait—reach out to us right away.
              </p>
            </Reveal>

            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {EMERGENCIES.map((e, i) => (
                <Reveal key={e.label} delay={i * 0.06}>
                  <div
                    className="flex items-center gap-4 rounded-2xl border border-[var(--foam)]/12 bg-[#0b1024]/40 px-5 py-4 backdrop-blur-xl transition-all duration-300 hover:border-[var(--aqua)]/40"
                    data-testid={`emergency-item-${i}`}
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[var(--aqua)]/25 bg-[var(--aqua)]/10 text-[var(--aqua)]">
                      <e.icon className="h-5 w-5" strokeWidth={1.6} />
                    </div>
                    <span className="font-body text-sm font-light text-[var(--foam)]">{e.label}</span>
                    <Check className="ml-auto h-4 w-4 text-[var(--aqua)]/70" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* PREMIUM STATEMENT */}
        <section className="px-6 lg:px-16 pb-24">
          <div className="mx-auto max-w-4xl">
            <Reveal>
              <blockquote
                className="rounded-3xl border border-[var(--foam)]/10 bg-[#0b1024]/35 px-8 py-16 text-center backdrop-blur-2xl"
                data-testid="emergency-statement"
              >
                <p className="font-title text-2xl font-bold leading-snug tracking-tight lg:text-4xl">
                  "Your aquarium is a living ecosystem.<br className="hidden sm:block" /> When every minute matters, Seatech is ready to respond."
                </p>
              </blockquote>
            </Reveal>
          </div>
        </section>

        {/* CTA */}
        <section className="px-6 lg:px-16 pb-32">
          <div className="mx-auto max-w-3xl text-center">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-5xl" data-testid="emergency-cta-heading">
                Need Emergency Support?
              </h2>
              <p className="mx-auto mt-5 max-w-xl font-body text-sm font-light leading-relaxed text-[var(--foam-dim)] lg:text-base">
                If you're currently enrolled in a Seatech Aquarium Maintenance Program, contact us
                immediately and we'll begin assisting you as quickly as possible.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => goHome("#contact")}
                  data-testid="emergency-cta-call"
                  className="group inline-flex items-center gap-3 rounded-full bg-[var(--aqua)] px-9 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[var(--ink-900)] transition-all duration-300 hover:shadow-[0_0_44px_-8px_var(--aqua)]"
                >
                  <Phone className="h-4 w-4" />
                  Call Emergency Support
                </button>
                <button
                  onClick={() => goHome("#about")}
                  data-testid="emergency-cta-plans"
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--foam)]/25 px-8 py-4 font-body text-sm font-light uppercase tracking-[0.16em] text-[var(--foam)]/90 backdrop-blur-md transition-all duration-300 hover:border-[var(--foam)]/60"
                >
                  View Maintenance Plans
                </button>
              </div>
            </Reveal>
          </div>
        </section>
      </motion.div>
    </main>
  );
}
